import { Notification } from "@screencloud/screencloud-ui-components";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useEffect } from "react";
import { FormattedMessage, InjectedIntlProps, injectIntl } from "react-intl";
import { appConfig } from "src/appConfig";
import { formatted } from "src/billinglatest/util/currency";
import { useAppContext } from "src/hooks/useAppContext";
import { countryName } from "../../countryName";
import { resolveEffectiveShipping } from "../../effectiveShipping";
import {
  emptyPixiAddress,
  type PixiAddress,
  type StepProps,
} from "../../hooks/usePixiWizardState";
import { DISCOUNT_PERCENT } from "../PlanBillingStep/constants";
import { OrderSummary } from "./OrderSummary";
import { ReviewCard } from "./ReviewCard";
import { clearPurchaseResume, consumeResumeNotice } from "./purchaseResume";
import { useCheckoutEstimate } from "./useCheckoutEstimate";
import { useCheckoutPricing } from "./useCheckoutPricing";
import { usePurchase } from "./usePurchase";
import {
  AddressBlock,
  AddressGroup,
  AddressLabel,
  AddressText,
  CheckoutLayout,
  PurchaseLoadingOverlay,
  DiscountRow,
  Label,
  MutedRow,
  ReviewColumn,
  Row,
  SavingsRow,
  SummaryColumn,
  Value,
} from "./styles";

const NUMBER_OF_SCREENS_STEP = 1;
const PLAN_BILLING_STEP = 2;
const PAYMENT_SHIPPING_STEP = 3;

/**
 * Initialise Stripe once at module scope (mirrors PaymentShippingStep). The
 * Checkout step needs its own `Elements` provider so the purchase submit can run
 * the 3DS/SCA challenge via `useStripe` — Chargebee charges through Stripe.
 */
const stripePromise = loadStripe(appConfig.stripePublicKey);

const addressLines = (address?: PixiAddress): string[] => {
  if (!address) return [];
  const name = [address.firstName, address.secondName]
    .filter(Boolean)
    .join(" ");
  const cityLine = [address.city, address.state, address.postalCode]
    .filter(Boolean)
    .join(", ");
  const streetLine = [address.building, address.street]
    .filter(Boolean)
    .join(" ");
  // Display the full country name (e.g. "Thailand"), consistent with the form
  // step's country <select> label. Wizard state keeps the raw ISO code; this is
  // display-only. Empty codes stay empty so the falsy filter drops the line.
  const country = address.country ? countryName(address.country) : "";
  return [name, address.company, streetLine, cityLine, country].filter(
    (line): line is string => Boolean(line),
  );
};

const CheckoutStepInner = ({
  state,
  updateState,
  goToStep,
  intl,
}: StepProps & InjectedIntlProps) => {
  const context = useAppContext();
  const {
    purchase,
    loading: purchasing,
    message: purchaseMessage,
    error,
  } = usePurchase(intl);

  // On mount, surface any one-shot notice left by an abandoned/failed redirect
  // resume, then drop any stale resume marker so a fresh checkout never resumes
  // an old redirect.
  useEffect(() => {
    const notice = consumeResumeNotice();
    if (notice) {
      Notification.warning({ message: notice });
    }
    clearPurchaseResume();
  }, []);

  const pricing = useCheckoutPricing(state);
  const {
    planId,
    planContent,
    currency,
    billingCycle,
    licenses,
    isFreeDevice,
    isDiscount,
    licencesCents,
    deviceSavingsCents,
    discountCents,
    shippingCents,
    shippingService,
    grandTotalCents,
  } = pricing;

  // The Payment & Shipping step (step 3) populates these before Checkout is
  // reachable (the footer gates Continue on a valid billing address), so the
  // empty-address fallbacks are just type guards, never shown in practice.
  const billingAddress = state.billingAddress ?? emptyPixiAddress();
  // Effective shipping must agree with the Payment & Shipping step's derivation
  // (GRWBKK-1735), via the same helper: while "same address" is ticked it is the
  // billing address; once the user customises shipping the toggle unticks in
  // step 3, so here we use their entered shipping address instead.
  const shippingAddress = resolveEffectiveShipping({
    // Hardware ships only with the free device, and `isFreeDevice` is derived
    // from the valid deal (GRWBKK-1730) — so a stale free-device left over for
    // an `Other` country, the discount deal and Monthly (no free device) all
    // ship nothing, and this address is never carried into the bundle. Matches
    // the `needsShipping` gate on the Payment & Shipping step (GRWBKK-1735).
    needsShipping: isFreeDevice,
    useSameAddress: state.useSameAddressForShipping ?? false,
    billing: billingAddress,
    manualShipping: state.shippingAddress ?? emptyPixiAddress(),
  });

  const price = (cents: number) => formatted(currency, cents);

  // Tax comes from the backend estimate, never computed on the client
  // (GRWBKK-1745/1744). Fetches on mount and when the billing address / VAT
  // changes; the Total then includes the sum of the returned tax lines.
  const {
    taxLines,
    totalCents,
    loading: taxLoading,
    error: taxError,
  } = useCheckoutEstimate({
    pricing,
    billingAddress,
    shippingAddress,
    vatNumber: state.vatNumber,
    spaceId: context.currentSpace?.id,
  });
  // The estimate's total is the real charge (licence + shipping + tax); use it
  // once resolved and fall back to the client total until then / when there's
  // no estimate (no billing country yet), so the summary always shows an amount.
  const displayTotalCents = totalCents ?? grandTotalCents;

  const editLabel = intl.formatMessage({
    id: "pixi_wizard.checkout.edit",
    defaultMessage: "Edit",
  });
  const billingType = intl.formatMessage(
    billingCycle === "year"
      ? { id: "pixi_wizard.checkout.cycle_annual", defaultMessage: "Annual" }
      : { id: "pixi_wizard.checkout.cycle_monthly", defaultMessage: "Monthly" },
  );

  const onPurchase = async () => {
    // The Stripe card token lives in memory only (dropped on close/reopen — see
    // usePixiWizardState PERSIST_OMIT). If a card payment has no live token, or
    // no method was chosen, send the user back to the Payment step to (re-)enter
    // it rather than assuming a default.
    const method = state.paymentMethod;
    if (!method || (method === "credit_card" && !state.cardPaymentMethodId)) {
      Notification.warning({
        message: intl.formatMessage({
          id: "pixi_wizard.checkout.card_reenter",
          defaultMessage:
            "Please re-enter your payment details to complete your purchase.",
        }),
      });
      goToStep?.(PAYMENT_SHIPPING_STEP);
      return;
    }

    // Convert the trial → paid: updateCustomerAddress → attach card →
    // updateSubscription → startSubscription. For the free-device deal the start
    // call carries the PIXI bundle (free device + paid shipping, both resolved by
    // the backend). `currency` is the trialist's locked currency — no fallback.
    const result = await purchase({
      planGroup: planId,
      billingCycle,
      currency,
      licenses,
      paymentMethod: method,
      cardPaymentMethodId: state.cardPaymentMethodId,
      billingAddress,
      vatNumber: state.vatNumber,
      poNumber: state.poNumber,
      // Only bundle when the destination actually has a shipping rate; the
      // service comes from that rate (standard/express as offered), never
      // assumed. No rate → no bundle (unsupported destination, blocked earlier).
      bundle:
        isFreeDevice && shippingService
          ? { shippingAddress, shippingService }
          : undefined,
    });
    if (result === "success") {
      // Card/invoice completed inline: flip to the Complete screen (CompleteStep),
      // carrying the tax-inclusive total the user just saw so the success screen
      // shows exactly what was charged without re-fetching the estimate
      // (GRWBKK-1820).
      updateState({ purchased: true, purchasedTotalCents: displayTotalCents });
    }
    // "redirected": the page is navigating away to the payment gateway — nothing
    // to do. "failed": the message surfaces inline via `error` (useError pattern).
  };

  return (
    <CheckoutLayout data-testid="checkout-step">
      {/* Staged purchase progress — reuses the old billing modal's loader overlay
          (LoaderBar + spinner + message) while the purchase chain runs. The
          message is swapped per API stage inside usePurchase (GRWBKK-1737). */}
      {purchasing && (
        <PurchaseLoadingOverlay overlay data-testid="checkout-purchase-loading">
          {purchaseMessage}
        </PurchaseLoadingOverlay>
      )}
      <ReviewColumn>
        <ReviewCard
          titleId="pixi_wizard.checkout.screens.title"
          titleDefault="Number of Screens"
          editLabel={editLabel}
          onEdit={() => goToStep?.(NUMBER_OF_SCREENS_STEP)}
          data-testid="review-screens"
        >
          <Row>
            <Label>
              <FormattedMessage
                id="pixi_wizard.checkout.screens.licenses"
                defaultMessage="x{count} {count, plural, one {Screen Licence} other {Screen Licences}}"
                values={{ count: licenses }}
              />
            </Label>
            <Value data-testid="review-screens-price">
              {price(licencesCents)}
            </Value>
          </Row>
        </ReviewCard>

        {isDiscount && (
          <ReviewCard
            titleId="pixi_wizard.checkout.discount.title"
            titleDefault="Discount"
            editLabel={editLabel}
            onEdit={() => goToStep?.(PLAN_BILLING_STEP)}
            data-testid="review-discount"
          >
            <Row>
              <Label>
                <FormattedMessage
                  id="pixi_wizard.checkout.discount.amount_label"
                  defaultMessage="Discount amount"
                />
              </Label>
              <Value data-testid="review-discount-percent">
                {DISCOUNT_PERCENT}%
              </Value>
            </Row>
            <DiscountRow data-testid="review-discount-amount">
              <FormattedMessage
                id="pixi_wizard.checkout.discount.amount"
                defaultMessage="Discount amount -{amount}"
                values={{ amount: price(discountCents) }}
              />
            </DiscountRow>
          </ReviewCard>
        )}

        <ReviewCard
          titleId="pixi_wizard.checkout.plan.title"
          titleDefault="Plan & Deals"
          editLabel={editLabel}
          onEdit={() => goToStep?.(PLAN_BILLING_STEP)}
          data-testid="review-plan"
        >
          <Row>
            <Label>
              <FormattedMessage
                id="pixi_wizard.checkout.plan.plan"
                defaultMessage="Plan"
              />
            </Label>
            <Value data-testid="review-plan-name">{planContent.name}</Value>
          </Row>
          <Row>
            <Label>
              <FormattedMessage
                id="pixi_wizard.checkout.plan.billing_type"
                defaultMessage="Billing Type"
              />
            </Label>
            <Value>{billingType}</Value>
          </Row>
          <Row>
            <Label>
              <FormattedMessage
                id="pixi_wizard.checkout.plan.currency"
                defaultMessage="Currency"
              />
            </Label>
            <Value>{currency}</Value>
          </Row>
        </ReviewCard>

        {isFreeDevice && (
          <ReviewCard
            titleId="pixi_wizard.checkout.device.title"
            titleDefault="Device"
            editLabel={editLabel}
            onEdit={() => goToStep?.(PLAN_BILLING_STEP)}
            data-testid="review-device"
          >
            <Row>
              <Label data-testid="review-device-count">
                <FormattedMessage
                  id="pixi_wizard.checkout.device.free_count"
                  defaultMessage="x{count} {count, plural, one {Free device} other {Free devices}}"
                  values={{ count: licenses }}
                />
              </Label>
              <Value data-testid="review-device-price">{price(0)}</Value>
            </Row>
            <SavingsRow data-testid="review-device-offer">
              <FormattedMessage
                id="pixi_wizard.checkout.device.free"
                defaultMessage="{count, plural, one {Free Device} other {Free Devices}} -{amount}"
                values={{
                  count: licenses,
                  amount: price(deviceSavingsCents),
                }}
              />
            </SavingsRow>
            <MutedRow data-testid="review-device-shipping">
              <FormattedMessage
                id="pixi_wizard.checkout.device.shipping"
                defaultMessage="Shipping +{amount}"
                values={{ amount: price(shippingCents) }}
              />
            </MutedRow>
          </ReviewCard>
        )}

        <ReviewCard
          // The discount deal ships no hardware, so the review card drops the
          // shipping block and titles itself "Billing address". Only the
          // free-device deal shows the full "Payment & Shipping" card — mirrors
          // the `needsShipping` gate on the Payment & Shipping wizard step.
          titleId={
            isFreeDevice
              ? "pixi_wizard.checkout.payment.title"
              : "pixi_wizard.checkout.payment.billing_title"
          }
          titleDefault={isFreeDevice ? "Payment & Shipping" : "Billing address"}
          editLabel={editLabel}
          onEdit={() => goToStep?.(PAYMENT_SHIPPING_STEP)}
          data-testid="review-payment"
        >
          <AddressGroup>
            <AddressBlock>
              <AddressLabel>
                <FormattedMessage
                  id="pixi_wizard.checkout.payment.billing"
                  defaultMessage="Billing address"
                />
              </AddressLabel>
              {addressLines(billingAddress).map((line) => (
                <AddressText key={line}>{line}</AddressText>
              ))}
            </AddressBlock>
            {isFreeDevice && (
              <AddressBlock data-testid="review-shipping-block">
                <AddressLabel>
                  <FormattedMessage
                    id="pixi_wizard.checkout.payment.shipping"
                    defaultMessage="Shipping information"
                  />
                </AddressLabel>
                {addressLines(shippingAddress).map((line) => (
                  <AddressText key={line}>{line}</AddressText>
                ))}
              </AddressBlock>
            )}
          </AddressGroup>
        </ReviewCard>
      </ReviewColumn>

      <SummaryColumn>
        <OrderSummary
          planName={planContent.name}
          licenses={licenses}
          licensesPrice={price(licencesCents)}
          freeDevices={isFreeDevice}
          deviceCount={licenses}
          deviceCostPrice={price(0)}
          discountPrice={isDiscount ? price(discountCents) : undefined}
          shippingPrice={price(shippingCents)}
          taxLines={taxLines.map((tax) => ({
            label: tax.label,
            amount: price(tax.amount),
          }))}
          taxLoading={taxLoading}
          taxError={taxError}
          total={price(displayTotalCents)}
          purchasing={purchasing}
          onPurchase={onPurchase}
          error={error}
        />
      </SummaryColumn>
    </CheckoutLayout>
  );
};

/**
 * Provides the Stripe `Elements` context so the inner step's purchase submit can
 * run the 3DS/SCA challenge via `useStripe`.
 */
const CheckoutStepComponent = (props: StepProps & InjectedIntlProps) => (
  <Elements stripe={stripePromise}>
    <CheckoutStepInner {...props} />
  </Elements>
);

export const CheckoutStep = injectIntl(CheckoutStepComponent);
